import { concat, find, findIndex, findLast, isEmpty, isNil, keyBy, toNumber } from 'lodash'
import commonMutations from '@/store/data/common/mutations'
import Vue from 'vue'

const noItem = item => isEmpty(item) || isNil(item)

const mutations = {
    ...commonMutations,

    setAudienceVolumes(state, { audienceId, volumes, volumeType }) {
        const _audience = find(state.list, item => item.id === audienceId)
        //set all volumes
        _audience.volumes = volumes
        //set volume to display in table
        if (isNil(volumeType)) _audience.volume = 'n/a'
        else if (typeof volumeType === 'string') _audience.volume = volumes[volumeType]
        else if (Array.isArray(volumeType)) {
            const xDevice = volumeType[0]
            const xDeviceVolume = volumeType[1]

            if (noItem(volumes[xDevice]) || isNil(volumes[xDevice][xDeviceVolume])) _audience.volume = 'n/a'
            else _audience.volume = volumes[xDevice][xDeviceVolume]
        }
    },

    setEmptyVolumes(state) {
        for (const idx in state.list) {
            delete state.list[idx].volumes
        }
    },

    setAudienceSummary(state, { audienceId, summary }) {
        const _audience = find(state.list, item => item.id === audienceId)
        _audience.summary = summary
    },

    setAudienceBreakdown(state, { audienceId, breakdown }) {
        const _audience = find(state.list, item => item.id === audienceId)
        _audience.breakdown = breakdown
    },

    setAudienceDetails(state, { items, audienceId }) {
        state.audienceDetails = {
            ...state.audienceDetails,
            [audienceId]: items
        }
    },

    setAudienceVolumeAlert(state, { audienceId, volumeAlert }) {
        const audience = state.list.find(audience => audience.id == audienceId)
        audience.volumeAlert = { ...audience.volumeAlert, ...volumeAlert }
    },

    setNewAudienceTransfer(state, { audienceId, transfer }) {
        //findLast because mutation usually called just after audience creation => new audience most likelly to be at the end of state.list
        const _audience = findLast(state.list, item => toNumber(item.id) === toNumber(audienceId))
        const _transfers = concat(_audience.children, transfer)
        Vue.set(_audience, 'children', _transfers)
    },

    seEditedAudienceTransfers(state, { audienceId, transfers }) {
        const _audience = find(state.list, item => toNumber(item.id) === toNumber(audienceId))
        Vue.set(_audience, 'children', transfers)
    },

    /**
     * Set an audience that has been edited
     *
     * @param {Object} state - vuex state for audience module
     * @param {Object} payload - The retrieved audience returned by the API
     * @param {Object} payload.audience - The retrieved audience returned by the API
     * @param {Boolean} payload.persist - We persist the volumes because the API won't retrieve the volumes and would therefore overide the volumee. Persisting the data will allow us to avoid re-requesting the API
     */
    setEditedAudience(state, { audience, persist = true }) {
        const audienceIndex = state.list.findIndex(item => item.id === audience.id)
        if (audienceIndex >= 0) {
            const oldAudience = state.list[audienceIndex]
            const volume = persist ? oldAudience.volume : null
            const volumes = persist ? oldAudience.volumes : null
            const summary = persist ? oldAudience.summary : null
            Vue.set(state.list, audienceIndex, {
                ...audience,
                volume,
                volumes,
                summary
            })
        }
    },
    setChangeRowData(state, rowData) {
        state.changeRowData = rowData
    },

    setEditedAudienceSpecificField(state, { audienceId, field, value }) {
        const selectedAudience = find(state.list, item => item.id === audienceId)
        const selectedAudienceIndex = findIndex(state.list, item => item.id === audienceId)
        selectedAudience[field] = value
        const updatedList = state.list
        updatedList.splice(selectedAudienceIndex, 1, selectedAudience)
        Vue.set(state, 'list', updatedList)
    },

    removeAudience(state, { audienceId }) {
        const indexOfAudienceToRemove = findIndex(state.list, item => toNumber(item.id) === toNumber(audienceId))
        Vue.delete(state.list, indexOfAudienceToRemove)
    },

    setAudienceSharingAccounts(state, { audienceId, sharingAccountslist }) {
        const _audience = find(state.list, item => item.id === audienceId)
        _audience.sharingAccountslist = sharingAccountslist
    },

    setCollection(state, data) {
        Vue.set(state, 'collection', keyBy(data, 'crossId'))
    },

    setAudienceInCollection(state, audience) {
        Vue.set(state.collection, audience.crossId, audience)
    },

    removeAudienceInCollection(state, id) {
        Vue.delete(state.collection, id)
    },

    addAudienceTagInCollection(state, { audienceId, newTags }) {
        const audienceBuilder = state.collection[`Audience_${audienceId}`]
        const audience = audienceBuilder || state.list.find(aud => aud.id == audienceId)
        const tags = [...audience.tags, ...newTags]
        if (audienceBuilder) audience.edit({ tags })
    },

    removeAudienceTagInColltection(state, { audienceId, tagId }) {
        const audience = state.collection[`Audience_${audienceId}`]
        const tags = audience.tags.filter(tag => tag.id != tagId)
        audience.edit({ tags })
    },

    setIsUpdatedOrDeletedLoading(state, loading) {
        state.isUpdatedOrDeletedLoading = loading
    },

    setResetBulkSelectedRows(state, reset) {
        state.resetBulkSelectedRows = reset
    },

    resetFormula(state, value) {
        state.resetFormula = value
    },

    setAudiencesActiveError(state, error) {
        state.audiencesActiveError = error
    }
}

export default mutations
